import { memo, useEffect, useRef, useState, type CSSProperties, type DragEvent, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { MediaKind, MediaLibrary, MediaResource, PresentationItemInput, Song } from '../../shared/api/types';
import { Alert, Button } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { mediaFileUrl } from '../media/MediaPage';
import { isEwThumbnail, localApi, localUrl, type LocalFile } from './local';
import { VideoThumb } from './VideoThumb';
import { WordTab, type ProjectScripture } from './WordTab';
import { WithPreview } from './WithPreview';
import { PdfPage } from './PdfPage';
import { SourcesTab } from './SourcesTab';
import { EditDialog, useContextMenu } from './ContextMenu';
import { useMediaFolders } from './mediaFolders';
import { loadRecent, saveRecent, youtubeApi, youtubeId, youtubeThumb, youtubeTitle, type RecentVideo } from './youtube';

/** Lo que se arrastra de una miniatura a una subcarpeta (la clave del archivo). */
const MEDIA_DRAG = 'application/x-keora-media';

/** Lo que se arrastra de la biblioteca a Programa. */
export const DRAG_TYPE = 'application/x-keora-item';

export type NewItem = Pick<PresentationItemInput, 'kind' | 'title'> & Partial<PresentationItemInput>;
export type BackgroundPick = { resourceId: string | null; localFile: string | null; camera?: string | null };
type Add = (item: NewItem | NewItem[]) => Promise<void>;
type SetBackground = (pick: BackgroundPick) => Promise<void>;

const TABS = [
  { key: 'cantos', label: 'Cantos' },
  { key: 'palabra', label: 'Biblia' },
  { key: 'multimedia', label: 'Multimedia' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'fuentes', label: 'Cámaras y entradas' },
  { key: 'mas', label: 'Avisos y más' },
] as const;
type Tab = (typeof TABS)[number]['key'];

const TAB_KEY = 'keora.consola.pestana';

/**
 * Parte de abajo de la consola, como en EasyWorship: Cantos, Biblia y Multimedia. Todo lo que se
 * proyecta se arrastra a Programa (o con «A Programa»). En Multimedia, doble clic en un video o una
 * imagen lo pone al instante de fondo detrás de la letra.
 */
export function ConsoleLibrary({ churchId, onAdd, onBackground, onScripture, extras }: { churchId: string; onAdd: Add; onBackground: SetBackground; onScripture: ProjectScripture; extras: ReactNode }) {
  const [tab, setTab] = useState<Tab>(() => {
    try {
      return TABS.find((t) => t.key === localStorage.getItem(TAB_KEY))?.key ?? 'cantos';
    } catch {
      return 'cantos';
    }
  });
  const choose = (t: Tab) => {
    setTab(t);
    try {
      localStorage.setItem(TAB_KEY, t);
    } catch {
      // Sin almacenamiento: solo no se recuerda.
    }
  };
  return (
    <section className="ewc__library" aria-label="Biblioteca">
      <div className="ewc__tabs" role="tablist" aria-label="Biblioteca">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`ewc__tab${tab === t.key ? ' ewc__tab--active' : ''}`} onClick={() => choose(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="ewc__tabpanel" role="tabpanel" aria-label={TABS.find((t) => t.key === tab)?.label}>
        {tab === 'cantos' ? <SongsTab churchId={churchId} onAdd={onAdd} /> : null}
        {tab === 'palabra' ? <WordTab onAdd={onAdd} onProject={onScripture} /> : null}
        {tab === 'multimedia' ? <MediaTab churchId={churchId} onAdd={onAdd} onBackground={onBackground} /> : null}
        {tab === 'youtube' ? <YouTubeTab onAdd={onAdd} /> : null}
        {tab === 'fuentes' ? <SourcesTab onAdd={onAdd} onBackground={onBackground} /> : null}
        {tab === 'mas' ? extras : null}
      </div>
    </section>
  );
}

/** Quita acentos y mayúsculas para buscar «cuan grande» y encontrar «Cuán Grande Es Él». */
const plain = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const dragProps = (item: NewItem) => ({
  draggable: true,
  onDragStart: (e: DragEvent) => {
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(item));
    e.dataTransfer.setData('text/plain', item.title);
    e.dataTransfer.effectAllowed = 'copy';
  },
});

/** Agregar a Programa (o poner de fondo) mostrando el error o lo que pasó. */
function useAction() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  async function run(key: string, job: () => Promise<void>, message: string) {
    setBusy(key);
    setError(null);
    setDone(null);
    try {
      await job();
      setDone(message);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }
  const status = (
    <>
      {error ? <Alert>{error}</Alert> : null}
      {done ? (
        <p className="small ewc__done" role="status">
          {done}
        </p>
      ) : null}
    </>
  );
  return { busy, run, status };
}

const blankSong: Song = { id: '', title: '', author: null, lyrics: '', key: null, bpm: null, version: null, notes: null, links: [], archived: false, updatedAt: '' };

// memo: la consola se vuelve a pintar cada segundo y medio (estado en vivo); la biblioteca no cambia por eso.
const SongsTab = memo(function SongsTab({ churchId, onAdd }: { churchId: string; onAdd: Add }) {
  const songs = useAsync((signal) => api.get<{ items: Song[] }>(`/worship/churches/${churchId}/songs`, signal).then((r) => r.items.filter((s) => !s.archived)), [churchId]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const { busy, run, status } = useAction();
  const list = songs.status === 'ready' ? songs.data : [];
  const shown = q.trim() ? list.filter((s) => plain(`${s.title} ${s.author ?? ''} ${s.lyrics}`).includes(plain(q.trim()))) : list;
  const current = list.find((s) => s.id === selected) ?? null;
  const item = (s: Song): NewItem => ({ kind: 'lyrics', title: s.title.slice(0, 120), body: s.lyrics.trim() || null });
  const add = (s: Song) => void run(s.id, () => onAdd(item(s)), `«${s.title}» está en Programa.`);
  const menu = useContextMenu();
  // Canto que se edita; `null` en id es uno nuevo.
  const [editing, setEditing] = useState<Song | null>(null);
  const base = `/worship/churches/${churchId}/songs`;
  const remove = (s: Song) => {
    if (!window.confirm(`¿Archivar «${s.title}»? Ya no aparecerá en los cantos de la iglesia; se puede recuperar desde Cantos (archivados).`)) return;
    void run(
      s.id,
      async () => {
        await api.post(`${base}/${s.id}/archive`, {});
        if (selected === s.id) setSelected(null);
        songs.reload();
      },
      `«${s.title}» se archivó.`,
    );
  };
  const songMenu = (e: React.MouseEvent, s: Song) => {
    setSelected(s.id);
    menu.open(e, s.title, [
      { label: 'A Programa', onSelect: () => add(s) },
      { label: 'Editar letra…', onSelect: () => setEditing(s) },
      { label: 'Nuevo canto…', onSelect: () => setEditing(blankSong) },
      { label: 'Archivar', danger: true, onSelect: () => remove(s) },
    ]);
  };

  return (
    <div className="ewc__split">
      {menu.element}
      {editing ? (
        <EditDialog
          title={editing.id ? `Editar «${editing.title}»` : 'Nuevo canto'}
          fields={[
            { name: 'title', label: 'Título', value: editing.title, required: true },
            { name: 'author', label: 'Autor', value: editing.author ?? '' },
            { name: 'lyrics', label: 'Letra (una línea en blanco separa las estrofas)', value: editing.lyrics, multiline: true },
          ]}
          onSave={async (v) => {
            const body = { title: v.title!.trim(), author: v.author!.trim() || null, lyrics: v.lyrics!, key: editing.key, bpm: editing.bpm, notes: editing.notes, links: editing.links, version: editing.version ?? null };
            if (editing.id) await api.put(`${base}/${editing.id}`, body);
            else setSelected((await api.post<{ id: string }>(base, body)).id);
            songs.reload();
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <div className="ewc__listpane">
        <div className="ewc__search">
          <label className="visually-hidden" htmlFor="ewc-song-search">
            Buscar canto
          </label>
          <input id="ewc-song-search" type="search" value={q} placeholder="Buscar por título, autor o letra" onChange={(e) => setQ(e.target.value)} />
          <span className="small muted">
            {shown.length} {shown.length === 1 ? 'canto' : 'cantos'}
          </span>
          <button type="button" className="link-button" onClick={() => setEditing({ ...blankSong, title: q.trim() })}>
            Nuevo canto
          </button>
        </div>
        {status}
        {songs.status === 'loading' ? <p className="small muted">Cargando cantos…</p> : null}
        <ul className="ewc__rows" aria-label="Cantos">
          {shown.slice(0, 300).map((s) => (
            <li key={s.id}>
              <button type="button" className={`ewc__row${selected === s.id ? ' ewc__row--selected' : ''}`} aria-pressed={selected === s.id} onClick={() => setSelected(s.id)} onDoubleClick={() => add(s)} onContextMenu={(e) => songMenu(e, s)} {...dragProps(item(s))}>
                <span className="ewc__row-title">{s.title}</span>
                <span className="ewc__row-sub">{s.author ?? ''}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="ewc__detail">
        {current ? (
          <>
            <div className="ewc__detail-head">
              <p className="ewc__detail-title">
                <strong>{current.title}</strong> {current.author ? <span className="muted small">· {current.author}</span> : null}
              </p>
              <Button busy={busy === current.id} onClick={() => add(current)}>
                A Programa
              </Button>
            </div>
            <pre className="ewc__lyrics">{current.lyrics}</pre>
          </>
        ) : (
          <p className="small muted">Busca el canto y arrástralo a Programa (o doble clic). Ahí eliges la estrofa para proyectarla.</p>
        )}
      </div>
    </div>
  );
});

/** Videos de YouTube: se pega el enlace y va a Programa; se reproduce desde YouTube, sin descargarlo. */
const YouTubeTab = memo(function YouTubeTab({ onAdd }: { onAdd: Add }) {
  const [url, setUrl] = useState('');
  const [recent, setRecent] = useState<RecentVideo[]>(loadRecent);
  const { busy, run, status } = useAction();
  const api = youtubeApi();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  useEffect(() => {
    void api?.signedIn().then(setSignedIn).catch(() => setSignedIn(null));
  }, [api]);
  const menu = useContextMenu();
  const [viewing, setViewing] = useState<RecentVideo | null>(null);
  const id = youtubeId(url);
  const item = (v: RecentVideo): NewItem => ({ kind: 'media', title: v.title.slice(0, 120), youtube: v.id });
  const remember = (v: RecentVideo) =>
    setRecent((list) => {
      const next = [v, ...list.filter((x) => x.id !== v.id)];
      saveRecent(next);
      return next;
    });
  const add = (v: RecentVideo) =>
    void run(
      `yt:${v.id}`,
      async () => {
        await onAdd(item(v));
        remember(v);
      },
      `«${v.title}» está en Programa.`,
    );
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!id) return;
    const title = (await youtubeTitle(id)) ?? 'Video de YouTube';
    add({ id, title });
    setUrl('');
  }
  async function session(action: 'login' | 'logout') {
    if (!api) return;
    await (action === 'login' ? api.login() : api.logout());
    setSignedIn(await api.signedIn().catch(() => null));
  }
  return (
    <WithPreview
      storageKey="keora.consola.vistaPrevia.youtube"
      preview={
        viewing ? (
          <div className="mediaprev">
            <div className="mediaprev__stage">
              {/* Solo en la consola: el reproductor de YouTube sin sonido, con sus controles. */}
              <iframe key={viewing.id} src={`https://www.youtube.com/embed/${viewing.id}?mute=1&rel=0&playsinline=1`} title={`Vista previa: ${viewing.title}`} allow="encrypted-media; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" />
            </div>
            <p className="mediaprev__name">{viewing.title}</p>
            <div className="mediaprev__actions">
              <button type="button" className="btn btn--secondary" onClick={() => add(viewing)}>
                A Programa
              </button>
            </div>
          </div>
        ) : (
          <p className="small muted withprev__empty">Toca un video de la lista para verlo aquí.</p>
        )
      }
    >
    <div className="ewc__word ewc__yt">
      {menu.element}
      <form onSubmit={(e) => void submit(e)}>
        <label htmlFor="ewc-youtube">Enlace de YouTube</label>
        <div className="ewc__search">
          <input id="ewc-youtube" type="url" value={url} placeholder="https://www.youtube.com/watch?v=…" autoComplete="off" aria-invalid={Boolean(url.trim()) && !id} onChange={(e) => setUrl(e.target.value)} />
          <Button type="submit" busy={busy !== null && busy === `yt:${id}`} disabled={!id}>
            A Programa
          </Button>
        </div>
      </form>
      {url.trim() && !id ? <p className="small">Ese enlace no es de un video de YouTube.</p> : null}
      <p className="small muted">Se reproduce desde YouTube, sin descargarlo (hace falta internet). YouTube decide si pone anuncios: con una cuenta de YouTube Premium abierta en esta computadora no salen.</p>
      {api ? (
        <p className="small ewc__yt-session">
          {signedIn ? 'Sesión de YouTube abierta en esta computadora.' : 'Sin sesión de YouTube en esta computadora.'}{' '}
          <button type="button" className="link-button" onClick={() => void session(signedIn ? 'logout' : 'login')}>
            {signedIn ? 'Cerrar sesión de YouTube' : 'Iniciar sesión en YouTube'}
          </button>
        </p>
      ) : (
        <p className="small muted">Para iniciar sesión en YouTube (y quitar anuncios con Premium) actualiza la app KEORA Multimedia a la versión 1.4.0.</p>
      )}
      {status}
      {recent.length > 0 ? (
        <>
          <p className="small muted">Usados en esta computadora (arrástralos a Programa o doble clic):</p>
          <ul className="ewc__thumbs" aria-label="Videos de YouTube">
            {recent.map((v) => (
              <li key={v.id} className="ewc__thumb-cell">
                <div
                  className="ewc__thumb"
                  title={v.title}
                  {...dragProps(item(v))}
                  onClick={() => setViewing(v)}
                  onDoubleClick={() => add(v)}
                  onContextMenu={(ev) =>
                    menu.open(ev, v.title, [
                      { label: 'A Programa', onSelect: () => add(v) },
                      {
                        label: 'Quitar de la lista',
                        danger: true,
                        onSelect: () =>
                          setRecent((list) => {
                            const next = list.filter((x) => x.id !== v.id);
                            saveRecent(next);
                            return next;
                          }),
                      },
                    ])
                  }
                >
                  <span className="ewc__thumb-pic">
                    <img src={youtubeThumb(v.id)} alt="" loading="lazy" draggable={false} />
                    <span className="ewc__thumb-badge">▶</span>
                  </span>
                  <span className="ewc__thumb-name">{v.title}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
    </WithPreview>
  );
});


const MEDIA_KINDS: { key: MediaKind; label: string }[] = [
  { key: 'video', label: 'Videos' },
  { key: 'image', label: 'Imágenes' },
  { key: 'audio', label: 'Audio' },
  { key: 'document', label: 'PDF' },
];

/** Tamaños de las miniaturas de Multimedia (ancho mínimo en rem) que se eligen con − y +. */
export const THUMB_SIZES = [4.5, 5.5, 6.5, 8, 10, 12.5, 15];
const THUMB_KEY = 'keora.consola.miniaturas';

export function useThumbSize() {
  const [size, setSize] = useState<number | null>(() => {
    try {
      const i = Number(localStorage.getItem(THUMB_KEY));
      return localStorage.getItem(THUMB_KEY) !== null && THUMB_SIZES[i] ? i : null;
    } catch {
      return null;
    }
  });
  const change = (step: number) => {
    const next = Math.min(THUMB_SIZES.length - 1, Math.max(0, (size ?? 2) + step));
    setSize(next);
    try {
      localStorage.setItem(THUMB_KEY, String(next));
    } catch {
      // Sin almacenamiento: solo no se recuerda.
    }
  };
  return { size, smaller: () => change(-1), bigger: () => change(1) };
}

type MediaEntry = { key: string; name: string; kind: MediaKind; src: string; local: boolean; item: NewItem; background: BackgroundPick };

const MediaTab = memo(function MediaTab({ churchId, onAdd, onBackground }: { churchId: string; onAdd: Add; onBackground: SetBackground }) {
  const [kind, setKind] = useState<MediaKind>('video');
  const [q, setQ] = useState('');
  const library = useAsync((signal) => api.get<MediaLibrary>(`/media/churches/${churchId}/resources`, signal).then((r) => r.items), [churchId]);
  const [local] = useState(localApi);
  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  useEffect(() => {
    void local?.list().then(setLocalFiles).catch(() => undefined);
  }, [local]);
  const { busy, run, status } = useAction();
  const thumbs = useThumbSize();

  const title = (name: string) => name.replace(/\.[^.]+$/, '').slice(0, 120) || 'Archivo';
  const entries: MediaEntry[] = [
    ...localFiles
      .filter((f) => !isEwThumbnail(f.name))
      .map((f) => ({ key: `local:${f.name}`, name: f.name, kind: f.kind as MediaKind, src: localUrl(f.name), local: true, item: { kind: 'media' as const, title: title(f.name), localFile: f.name }, background: { resourceId: null, localFile: f.name } })),
    ...(library.status === 'ready' ? library.data : []).map((r: MediaResource) => ({
      key: r.id,
      name: r.name,
      kind: r.kind,
      src: mediaFileUrl(churchId, r.id),
      local: false,
      item: { kind: 'media' as const, title: title(r.name), resourceId: r.id },
      background: { resourceId: r.id, localFile: null },
    })),
  ];
  const folders = useMediaFolders();
  // Subcarpeta abierta del tipo elegido; null = todos.
  const [folder, setFolder] = useState<string | null>(null);
  const [folderDialog, setFolderDialog] = useState<{ from: string | null } | null>(null);
  const [dropOn, setDropOn] = useState<string | null>(null);
  const openFolder = folder && folders.list(kind).includes(folder) ? folder : null;
  // Lo que está en una carpeta ya no sale en la raíz («Videos», «Imágenes»…): solo en su carpeta.
  // La búsqueda con texto sí busca en todo (raíz y carpetas) y cada resultado dice su carpeta.
  const count = (k: MediaKind) => entries.filter((e) => e.kind === k && !folders.folderOf(e.key, k)).length;
  const inFolder = (f: string) => entries.filter((e) => e.kind === kind && folders.folderOf(e.key, kind) === f).length;
  const searching = Boolean(q.trim());
  const shown = entries.filter(
    (e) => e.kind === kind && (searching ? plain(e.name).includes(plain(q.trim())) : openFolder ? folders.folderOf(e.key, kind) === openFolder : !folders.folderOf(e.key, kind)),
  );
  const canBackground = (e: MediaEntry) => e.kind === 'video' || e.kind === 'image';
  const asBackground = (e: MediaEntry) => void run(`fondo:${e.key}`, () => onBackground(e.background), `«${title(e.name)}» quedó de fondo detrás de la letra.`);
  const toProgram = (e: MediaEntry) => void run(e.key, () => onAdd(e.item), `«${title(e.name)}» está en Programa.`);
  const menu = useContextMenu();
  const [renaming, setRenaming] = useState<MediaEntry | null>(null);
  // Selección como en un explorador: Ctrl/Cmd + clic suma o quita, Shift + clic toma un rango y
  // arrastrar en un espacio vacío dibuja un rectángulo. Esc o un clic en vacío la quitan.
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const [band, setBand] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Al soltar el rectángulo llega un clic: no debe quitar lo que se acaba de seleccionar.
  const banded = useRef(false);
  const selection = shown.filter((e) => picked.has(e.key));
  // Lo que se ve en la vista previa: el último que se tocó (si sigue a la vista).
  const previewEntry = entries.find((e) => e.key === anchor && e.kind === kind) ?? null;
  /** Sobre lo que se actúa: los seleccionados si el clic fue en uno de ellos; si no, solo ese. */
  const targets = (e: MediaEntry) => (picked.has(e.key) && selection.length > 1 ? selection : [e]);
  useEffect(() => {
    setPicked(new Set());
    setAnchor(null);
  }, [kind, openFolder]);
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setPicked(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  function clickThumb(ev: React.MouseEvent, e: MediaEntry) {
    if (ev.shiftKey && anchor) {
      const a = shown.findIndex((x) => x.key === anchor);
      const b = shown.findIndex((x) => x.key === e.key);
      if (a >= 0 && b >= 0) {
        const range = shown.slice(Math.min(a, b), Math.max(a, b) + 1).map((x) => x.key);
        setPicked(new Set(ev.ctrlKey || ev.metaKey ? [...picked, ...range] : range));
        return;
      }
    }
    if (ev.ctrlKey || ev.metaKey) {
      const next = new Set(picked);
      if (next.has(e.key)) next.delete(e.key);
      else next.add(e.key);
      setPicked(next);
    } else setPicked(new Set([e.key]));
    setAnchor(e.key);
  }
  /** Rectángulo de selección: empieza en un espacio vacío de la lista. */
  function bandStart(ev: React.PointerEvent<HTMLUListElement>) {
    if (ev.button !== 0 || (ev.target as HTMLElement).closest('.ewc__thumb, button')) return;
    const list = listRef.current;
    if (!list) return;
    const box = list.getBoundingClientRect();
    const at = (x: number, y: number) => ({ x: x - box.left, y: y - box.top + list.scrollTop });
    const p0 = at(ev.clientX, ev.clientY);
    const keep = ev.ctrlKey || ev.metaKey ? new Set(picked) : new Set<string>();
    if (!(ev.ctrlKey || ev.metaKey)) setPicked(new Set());
    let moved = false;
    const move = (m: PointerEvent) => {
      const p1 = at(m.clientX, m.clientY);
      if (!moved && Math.abs(p1.x - p0.x) + Math.abs(p1.y - p0.y) < 4) return;
      moved = true;
      banded.current = true;
      const r = { x0: Math.min(p0.x, p1.x), y0: Math.min(p0.y, p1.y), x1: Math.max(p0.x, p1.x), y1: Math.max(p0.y, p1.y) };
      setBand(r);
      const hit = new Set(keep);
      list.querySelectorAll<HTMLElement>('[data-key]').forEach((el) => {
        const b = el.getBoundingClientRect();
        const t = { x0: b.left - box.left, y0: b.top - box.top + list.scrollTop, x1: b.right - box.left, y1: b.bottom - box.top + list.scrollTop };
        if (t.x0 < r.x1 && t.x1 > r.x0 && t.y0 < r.y1 && t.y1 > r.y0) hit.add(el.dataset.key!);
      });
      setPicked(hit);
    };
    const up = () => {
      setBand(null);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }
  const ext = (name: string) => /\.[^.]+$/.exec(name)?.[0] ?? '';
  const withExt = (wanted: string, from: string) => (wanted.toLowerCase().endsWith(ext(from).toLowerCase()) ? wanted : `${wanted}${ext(from)}`);
  const remove = (list: MediaEntry[]) => {
    const e = list[0]!;
    const question =
      list.length > 1
        ? `¿Quitar estos ${list.length} archivos? Los de esta computadora se borran de su carpeta; los de Multimedia de la iglesia se archivan (se pueden recuperar).`
        : e.local
          ? `¿Quitar «${e.name}» de esta computadora? Se borra de la carpeta de KEORA Multimedia.`
          : `¿Eliminar «${e.name}» de Multimedia de la iglesia? Se puede recuperar desde Multimedia (archivados).`;
    if (!window.confirm(question)) return;
    void run(
      `quitar:${e.key}`,
      async () => {
        for (const x of list) {
          if (x.local) setLocalFiles(await local!.remove(x.name));
          else await api.post(`/media/churches/${churchId}/resources/${x.key}/archive`, {});
        }
        if (list.some((x) => !x.local)) library.reload();
        setPicked(new Set());
      },
      list.length > 1 ? `Se quitaron ${list.length} archivos.` : `«${title(e.name)}» se quitó.`,
    );
  };
  const toProgramMany = (list: MediaEntry[]) => void run(`prog:${list[0]!.key}`, () => onAdd(list.map((x) => x.item)), `${list.length} archivos están en Programa.`);
  const moveMany = (list: MediaEntry[], f: string | null) => list.forEach((x) => folders.move(x.key, x.kind, f));
  const mediaMenu = (ev: React.MouseEvent, e: MediaEntry) => {
    const list = targets(e);
    if (!picked.has(e.key)) {
      setPicked(new Set([e.key]));
      setAnchor(e.key);
    }
    if (list.length > 1)
      return menu.open(ev, `${list.length} seleccionados`, [
        { label: `A Programa (${list.length})`, onSelect: () => toProgramMany(list) },
        ...folders.list(e.kind).map((f) => ({ label: `Mover a «${f}»`, onSelect: () => moveMany(list, f) })),
        ...(list.some((x) => folders.folderOf(x.key, x.kind)) ? [{ label: 'Sacar de la carpeta', onSelect: () => moveMany(list, null) }] : []),
        { label: `Eliminar (${list.length})`, danger: true, onSelect: () => remove(list) },
      ]);
    return menu.open(ev, title(e.name), [
      ...(canBackground(e) ? [{ label: 'Usar como fondo', onSelect: () => asBackground(e) }] : []),
      { label: 'A Programa', onSelect: () => toProgram(e) },
      ...folders
        .list(e.kind)
        .filter((f) => f !== folders.folderOf(e.key, e.kind))
        .map((f) => ({ label: `Mover a «${f}»`, onSelect: () => folders.move(e.key, e.kind, f) })),
      ...(folders.folderOf(e.key, e.kind) ? [{ label: 'Sacar de la carpeta', onSelect: () => folders.move(e.key, e.kind, null) }] : []),
      ...(!e.local || local?.rename ? [{ label: 'Cambiar nombre…', onSelect: () => setRenaming(e) }] : []),
      { label: e.local ? 'Quitar de esta computadora' : 'Eliminar', danger: true, onSelect: () => remove([e]) },
    ]);
  };

  return (
    <div className="ewc__media">
      {menu.element}
      {renaming ? (
        <EditDialog
          title="Cambiar nombre"
          fields={[{ name: 'name', label: 'Nombre', value: title(renaming.name), required: true }]}
          onSave={async (v) => {
            const wanted = v.name!.trim();
            if (renaming.local) {
              setLocalFiles(await local!.rename!(renaming.name, wanted));
              folders.rekey(renaming.key, `local:${withExt(wanted, renaming.name)}`, renaming.kind);
            }
            else {
              await api.put(`/media/churches/${churchId}/resources/${renaming.key}`, { name: withExt(wanted, renaming.name) });
              library.reload();
            }
          }}
          onClose={() => setRenaming(null)}
        />
      ) : null}
      {folderDialog ? (
        <EditDialog
          title={folderDialog.from ? `Cambiar nombre de «${folderDialog.from}»` : `Nueva carpeta en ${MEDIA_KINDS.find((k) => k.key === kind)?.label}`}
          fields={[{ name: 'name', label: 'Nombre de la carpeta', value: folderDialog.from ?? '', required: true }]}
          onSave={async (v) => {
            setFolder(folderDialog.from ? folders.rename(kind, folderDialog.from, v.name!) : folders.create(kind, v.name!));
          }}
          onClose={() => setFolderDialog(null)}
        />
      ) : null}
      <nav className="ewc__side" aria-label="Tipo de archivo">
        {MEDIA_KINDS.map((k) => (
          <div key={k.key} className="ewc__side-group">
            <button
              type="button"
              className={`ewc__side-item${kind === k.key && !openFolder ? ' ewc__side-item--active' : ''}`}
              aria-pressed={kind === k.key && !openFolder}
              onClick={() => {
                setKind(k.key);
                setFolder(null);
              }}
            >
              {k.label} <span className="ewc__count">{count(k.key)}</span>
            </button>
            {kind === k.key ? (
              <>
                {folders.list(k.key).map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`ewc__side-item ewc__side-folder${openFolder === f ? ' ewc__side-item--active' : ''}${dropOn === f ? ' ewc__side-folder--drop' : ''}`}
                    aria-pressed={openFolder === f}
                    title="Arrastra aquí archivos para moverlos a esta carpeta"
                    onClick={() => setFolder(f)}
                    onContextMenu={(ev) =>
                      menu.open(ev, f, [
                        { label: 'Cambiar nombre…', onSelect: () => setFolderDialog({ from: f }) },
                        {
                          label: 'Eliminar carpeta',
                          danger: true,
                          onSelect: () => {
                            if (!window.confirm(`¿Eliminar la carpeta «${f}»? Sus archivos no se borran: vuelven a ${k.label}.`)) return;
                            folders.remove(k.key, f);
                            if (openFolder === f) setFolder(null);
                          },
                        },
                      ])
                    }
                    onDragOver={(ev) => {
                      if (!ev.dataTransfer.types.includes(MEDIA_DRAG)) return;
                      ev.preventDefault();
                      ev.dataTransfer.dropEffect = 'copy';
                      setDropOn(f);
                    }}
                    onDragLeave={() => setDropOn(null)}
                    onDrop={(ev) => {
                      ev.preventDefault();
                      setDropOn(null);
                      for (const key of ev.dataTransfer.getData(MEDIA_DRAG).split('\n').filter(Boolean)) folders.move(key, k.key, f);
                      setPicked(new Set());
                    }}
                  >
                    <span>📁 {f}</span> <span className="ewc__count">{inFolder(f)}</span>
                  </button>
                ))}
                <button type="button" className="ewc__side-item ewc__side-folder ewc__side-new" onClick={() => setFolderDialog({ from: null })}>
                  + Nueva carpeta
                </button>
              </>
            ) : null}
          </div>
        ))}
      </nav>
      <WithPreview storageKey="keora.consola.vistaPrevia.multimedia" preview={<MediaPreview entry={previewEntry} folder={previewEntry ? folders.folderOf(previewEntry.key, previewEntry.kind) : null} onBackground={previewEntry && canBackground(previewEntry) ? () => asBackground(previewEntry) : null} onProgram={previewEntry ? () => toProgram(previewEntry) : null} />}>
      <div className="ewc__listpane">
        <div className="ewc__search">
          <label className="visually-hidden" htmlFor="ewc-media-search">
            Buscar archivo
          </label>
          <input id="ewc-media-search" type="search" value={q} placeholder="Buscar por nombre" onChange={(e) => setQ(e.target.value)} />
          {local ? (
            <button type="button" className="link-button" onClick={() => void local.add().then(setLocalFiles)}>
              Agregar archivos de esta computadora
            </button>
          ) : null}
          <span className="ewc__zoom" role="group" aria-label="Tamaño de las miniaturas">
            <button type="button" className="ewc__zoom-btn" aria-label="Miniaturas más chicas" title="Miniaturas más chicas" disabled={thumbs.size === 0} onClick={thumbs.smaller}>
              −
            </button>
            <button type="button" className="ewc__zoom-btn" aria-label="Miniaturas más grandes" title="Miniaturas más grandes" disabled={thumbs.size === THUMB_SIZES.length - 1} onClick={thumbs.bigger}>
              +
            </button>
          </span>
        </div>
        <p className="small muted">{kind === 'video' || kind === 'image' ? 'Doble clic: de fondo detrás de la letra · Arrastrar a Programa: para reproducirlo o mostrarlo solo' : 'Arrástralo a Programa para reproducirlo.'}</p>
        {status}
        {shown.length === 0 ? <p className="small muted">{searching
            ? 'Nada coincide con la búsqueda.'
            : openFolder
              ? `La carpeta «${openFolder}» está vacía. Arrastra aquí archivos (a la carpeta de la izquierda) o usa clic derecho → «Mover a…».`
              : entries.some((e) => e.kind === kind)
                ? 'Todo está en carpetas: ábrelas a la izquierda o busca por nombre.'
                : `No hay ${MEDIA_KINDS.find((k) => k.key === kind)?.label.toLowerCase()} todavía.`}</p> : null}
        <ul
          ref={listRef}
          onPointerDown={bandStart}
          onClick={(ev) => {
            if (banded.current) {
              banded.current = false;
              return;
            }
            if (!(ev.target as HTMLElement).closest('.ewc__thumb, button')) setPicked(new Set());
          }}
          aria-multiselectable
          className={`ewc__thumbs${thumbs.size !== null && (THUMB_SIZES[thumbs.size] ?? 6.5) < 6.5 ? ' ewc__thumbs--tiny' : ''}`} aria-label="Archivos" style={thumbs.size === null ? undefined : ({ '--ewc-thumb': `${THUMB_SIZES[thumbs.size]}rem` } as CSSProperties)}>
          {shown.map((e) => (
            <li key={e.key} className="ewc__thumb-cell">
              <div
                className={`ewc__thumb${picked.has(e.key) ? ' ewc__thumb--picked' : ''}`}
                title={e.name}
                data-key={e.key}
                role="option"
                aria-selected={picked.has(e.key)}
                draggable
                onClick={(ev) => clickThumb(ev, e)}
                onDragStart={(ev) => {
                  // Si se arrastra uno de los seleccionados, van todos (en su orden).
                  const list = targets(e);
                  ev.dataTransfer.setData(DRAG_TYPE, JSON.stringify(list.length > 1 ? list.map((x) => x.item) : e.item));
                  ev.dataTransfer.setData('text/plain', list.map((x) => x.name).join(', '));
                  ev.dataTransfer.effectAllowed = 'copy';
                  ev.dataTransfer.setData(MEDIA_DRAG, list.map((x) => x.key).join('\n'));
                }}
                onDoubleClick={() => (canBackground(e) ? asBackground(e) : toProgram(e))} onContextMenu={(ev) => mediaMenu(ev, e)}>
                <span className="ewc__thumb-pic">
                  {e.kind === 'image' ? <img src={e.src} alt="" loading="lazy" draggable={false} /> : e.kind === 'video' ? <VideoThumb src={e.src} /> : <span aria-hidden="true">{e.kind === 'audio' ? '♪' : 'PDF'}</span>}
                  {e.local ? (
                    <span className="ewc__thumb-badge" title="En esta computadora">
                      💻
                    </span>
                  ) : null}
                </span>
                <span className="ewc__thumb-name">{title(e.name)}</span>
                {searching && folders.folderOf(e.key, e.kind) ? <span className="ewc__thumb-folder">📁 {folders.folderOf(e.key, e.kind)}</span> : null}
              </div>
              <div className="ewc__thumb-actions">
                {canBackground(e) ? (
                  <button type="button" className="link-button" disabled={busy !== null} onClick={() => asBackground(e)}>
                    Fondo<span className="visually-hidden"> {e.name}</span>
                  </button>
                ) : null}
                <button type="button" className="link-button" disabled={busy !== null} onClick={() => toProgram(e)}>
                  A Programa<span className="visually-hidden"> {e.name}</span>
                </button>
              </div>
            </li>
          ))}
          {band ? <li className="ewc__band" aria-hidden="true" style={{ left: band.x0, top: band.y0, width: band.x1 - band.x0, height: band.y1 - band.y0 }} /> : null}
        </ul>
        {selection.length > 1 ? <p className="small muted ewc__picked">{selection.length} seleccionados · arrástralos a Programa o a una carpeta · Esc para quitar la selección</p> : null}
      </div>
      </WithPreview>
    </div>
  );
});

/** Vista previa de Multimedia (solo en la consola): el video o audio con sus controles y sin sonido al empezar, la imagen o la primera página del PDF. */
function MediaPreview({ entry: e, folder, onBackground, onProgram }: { entry: MediaEntry | null; folder: string | null; onBackground: (() => void) | null; onProgram: (() => void) | null }) {
  const [duration, setDuration] = useState<number | null>(null);
  useEffect(() => setDuration(null), [e?.key]);
  if (!e) return <p className="small muted withprev__empty">Toca un archivo para verlo aquí antes de proyectarlo.</p>;
  const name = e.name.replace(/\.[^.]+$/, '');
  const time = duration && Number.isFinite(duration) ? `${Math.floor(duration / 60)}:${String(Math.floor(duration % 60)).padStart(2, '0')}` : null;
  return (
    <div className="mediaprev">
      <div className="mediaprev__stage">
        {e.kind === 'video' ? <video key={e.key} src={e.src} controls muted playsInline preload="metadata" onLoadedMetadata={(ev) => setDuration(ev.currentTarget.duration)} /> : null}
        {e.kind === 'image' ? <img key={e.key} src={e.src} alt="" /> : null}
        {e.kind === 'audio' ? <audio key={e.key} src={e.src} controls preload="metadata" onLoadedMetadata={(ev) => setDuration(ev.currentTarget.duration)} /> : null}
        {e.kind === 'document' ? (e.local ? <span className="mediaprev__doc">PDF</span> : <PdfPage className="mediaprev__pdf" url={e.src} page={1} />) : null}
      </div>
      <p className="mediaprev__name" title={e.name}>
        {name}
      </p>
      <p className="small muted mediaprev__meta">
        {[time, folder ? `📁 ${folder}` : null, e.local ? 'En esta computadora' : 'Multimedia de la iglesia'].filter(Boolean).join(' · ')}
      </p>
      <div className="mediaprev__actions">
        {onBackground ? (
          <button type="button" className="btn btn--secondary" onClick={onBackground}>
            Fondo
          </button>
        ) : null}
        {onProgram ? (
          <button type="button" className="btn btn--secondary" onClick={onProgram}>
            A Programa
          </button>
        ) : null}
      </div>
    </div>
  );
}
